import { Restaurant } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Star, FileText, UtensilsCrossed, ShoppingCart, ChevronUp, ChevronDown } from "lucide-react";
import { cn, ensureExternalUrl } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface RestaurantCardHeaderProps {
  restaurant: Restaurant;
  isFavorite: boolean;
  isOpen: boolean;
  onToggleFavorite: (restaurantId: string) => void;
  onToggleOpen: () => void;
}

export function RestaurantCardHeader({
  restaurant,
  isFavorite,
  isOpen,
  onToggleFavorite,
  onToggleOpen,
}: RestaurantCardHeaderProps) {
  const isPro = (restaurant as any).is_pro;
  const iconColor = isPro ? "text-white" : "text-foreground";
  const iconDisabledColor = isPro ? "text-white/40" : "text-muted-foreground/40";
  const iconHover = isPro ? "hover:text-white/80" : "hover:text-primary";

  return (
    <TooltipProvider delayDuration={200} skipDelayDuration={100}>
      <div className={cn(
        "px-4 py-2 border-b",
        isPro ? "bg-pro-header" : "bg-[#E9F9D9]"
      )}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "h-8 w-8 flex-shrink-0 tour-favorite-star",
                    isFavorite ? "text-highlight" : isPro ? "text-white" : "text-muted-foreground"
                  )}
                  onClick={() => onToggleFavorite(restaurant.id)}
                >
                  <Star size={25} className={cn(isFavorite && "fill-current")} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-semibold">Kedvencnek jelölés</p>
                <p className="text-xs">A találati lista elejére sorolja a kiválasztott éttermet</p>
              </TooltipContent>
            </Tooltip>
            
            <h3 className={cn("text-lg font-semibold flex-1", isPro && "text-white")}>{restaurant.name}</h3>
          </div>
          
          <div className="flex items-center gap-1 tour-card-icons">
            <Tooltip>
              <TooltipTrigger asChild>
                {(restaurant as any).weekly_menu_url ? (
                  <a
                    href={ensureExternalUrl((restaurant as any).weekly_menu_url) || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    referrerPolicy="no-referrer"
                    className={cn(
                      "inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-accent",
                      iconColor, iconHover
                    )}
                  >
                    <FileText size={31} />
                  </a>
                ) : (
                  <span className={cn("inline-flex items-center justify-center h-8 w-8 cursor-not-allowed", iconDisabledColor)}>
                    <FileText size={31} />
                  </span>
                )}
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-semibold">Heti menü</p>
                <p className="text-xs">Kattints ide a teljes heti ajánlat megtekintéséhez!</p>
              </TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                {(restaurant as any).ala_carte_url ? (
                  <a
                    href={ensureExternalUrl((restaurant as any).ala_carte_url) || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    referrerPolicy="no-referrer"
                    className={cn(
                      "inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-accent",
                      iconColor, iconHover
                    )}
                  >
                    <UtensilsCrossed size={31} />
                  </a>
                ) : (
                  <span className={cn("inline-flex items-center justify-center h-8 w-8 cursor-not-allowed", iconDisabledColor)}>
                    <UtensilsCrossed size={31} />
                  </span>
                )}
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-semibold">A'la carte</p>
                <p className="text-xs">Kattints ide az étterem teljes étlapjának megtekintéséhez!</p>
              </TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                {(restaurant as any).webshop_url ? (
                  <a
                    href={ensureExternalUrl((restaurant as any).webshop_url) || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    referrerPolicy="no-referrer"
                    className={cn(
                      "inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-accent",
                      iconColor, iconHover
                    )}
                  >
                    <ShoppingCart size={31} />
                  </a>
                ) : (
                  <span className={cn("inline-flex items-center justify-center h-8 w-8 cursor-not-allowed", iconDisabledColor)}>
                    <ShoppingCart size={31} />
                  </span>
                )}
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-semibold">Webshop</p>
                <p className="text-xs">Kattints ide online rendelés leadásához!</p>
              </TooltipContent>
            </Tooltip>
            
            
            <Button
              variant="ghost"
              size="icon"
              className={cn("h-8 w-8 tour-collapse-btn", iconColor, iconHover)}
              onClick={onToggleOpen}
            >
              {isOpen ? <ChevronUp size={31} /> : <ChevronDown size={31} />}
            </Button>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
